type InlineNodeKind = 'em' | 'fragment' | 'link' | 'strong' | 'u';

interface InlineNode {
  children: LegacyRichTextNode[];
  href?: string;
  kind: InlineNodeKind;
  sourceTag: string;
}

export type LegacyRichTextNode = string | InlineNode | { kind: 'break' };

const INLINE_TAGS = new Map<string, InlineNodeKind>([
  ['b', 'strong'],
  ['strong', 'strong'],
  ['i', 'em'],
  ['em', 'em'],
  ['u', 'u'],
]);

const BLOCK_TAGS = new Set([
  'address',
  'blockquote',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'li',
  'ol',
  'p',
  'pre',
  'ul',
]);

const SUPPRESSED_TAGS = new Set([
  'embed',
  'iframe',
  'math',
  'object',
  'script',
  'style',
  'svg',
  'template',
]);

const decodeHtmlEntities = (text: string): string =>
  text.replace(/&(#(?:x[0-9a-f]+|\d+)|amp|apos|gt|lt|nbsp|quot);/gi, (entity, code: string) => {
    const normalizedCode = code.toLowerCase();
    const namedEntities: Record<string, string> = {
      amp: '&',
      apos: "'",
      gt: '>',
      lt: '<',
      nbsp: '\u00a0',
      quot: '"',
    };

    if (namedEntities[normalizedCode]) return namedEntities[normalizedCode];

    const isHex = normalizedCode.startsWith('#x');
    const value = Number.parseInt(normalizedCode.slice(isHex ? 2 : 1), isHex ? 16 : 10);
    if (!Number.isFinite(value) || value < 0 || value > 0x10ffff) return entity;

    try {
      return String.fromCodePoint(value);
    } catch {
      return entity;
    }
  });

const getSafeHref = (attributes: string): string | undefined => {
  const hrefMatch = attributes.match(/\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/i);
  const href = decodeHtmlEntities(hrefMatch?.[1] ?? hrefMatch?.[2] ?? hrefMatch?.[3] ?? '').trim();
  const hasControlCharacter = Array.from(href).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || codePoint === 0x7f;
  });

  if (!href || hasControlCharacter) return undefined;
  if (/^(?:\/|\?|#|\.\.\/|\.\/)/.test(href)) return href;

  try {
    const protocol = new URL(href, 'https://www.zeelool.com').protocol;
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(protocol) ? href : undefined;
  } catch {
    return undefined;
  }
};

/** UI 与 FAQ Schema 共用解析结果，保持存量标签、实体和危险内容过滤规则。 */
export const parseLegacyRichText = (value: string, allowLinks = false): LegacyRichTextNode[] => {
  const root: InlineNode = { children: [], kind: 'fragment', sourceTag: 'root' };
  const stack = [root];
  const suppressedTags: string[] = [];

  const append = (node: LegacyRichTextNode) => stack.at(-1)?.children.push(node);
  const closeTag = (sourceTag: string) => {
    const matchingIndex = stack.findLastIndex((node) => node.sourceTag === sourceTag);
    if (matchingIndex <= 0) return;

    while (stack.length - 1 >= matchingIndex) {
      const node = stack.pop();
      if (node) append(node);
    }
  };

  const tokens: string[] = value.match(/<[^>]*>|[^<]+|</g) ?? [];

  tokens.forEach((token) => {
    if (!token.startsWith('<') || token === '<') {
      if (suppressedTags.length === 0) append(decodeHtmlEntities(token));
      return;
    }

    const tagMatch = token.match(/^<\s*(\/?)\s*([a-z][\w:-]*)([\s\S]*?)>$/i);
    if (!tagMatch) return;

    const [, closingMarker, rawTagName, attributes] = tagMatch;
    const tagName = rawTagName.toLowerCase();
    const isClosing = closingMarker === '/';
    const isSelfClosing = /\/\s*>$/.test(token);

    if (suppressedTags.length > 0) {
      if (!isClosing && SUPPRESSED_TAGS.has(tagName) && !isSelfClosing) {
        suppressedTags.push(tagName);
      } else if (isClosing) {
        const matchingIndex = suppressedTags.lastIndexOf(tagName);
        if (matchingIndex >= 0) suppressedTags.splice(matchingIndex);
      }
      return;
    }

    if (!isClosing && SUPPRESSED_TAGS.has(tagName)) {
      if (!isSelfClosing) suppressedTags.push(tagName);
      return;
    }

    if (isClosing) {
      closeTag(tagName);
      if (BLOCK_TAGS.has(tagName)) append('\n');
      return;
    }

    if (tagName === 'br') {
      append({ kind: 'break' });
      return;
    }

    if (tagName === 'li') append('• ');

    const inlineKind = INLINE_TAGS.get(tagName);
    if (inlineKind) {
      stack.push({ children: [], kind: inlineKind, sourceTag: tagName });
      if (isSelfClosing) closeTag(tagName);
      return;
    }

    if (tagName === 'a') {
      const href = allowLinks ? getSafeHref(attributes) : undefined;
      stack.push({
        children: [],
        href,
        kind: href ? 'link' : 'fragment',
        sourceTag: tagName,
      });
      if (isSelfClosing) closeTag(tagName);
    }
  });

  while (stack.length > 1) {
    const node = stack.pop();
    if (node) append(node);
  }

  return root.children;
};

export function getLegacyRichTextPlainText(value: string): string {
  const text = (nodes: LegacyRichTextNode[]): string =>
    nodes
      .map((node) => {
        if (typeof node === 'string') return node;
        return node.kind === 'break' ? '\n' : text(node.children);
      })
      .join('');
  // HTML 文本节点解析会丢弃 NUL，Schema 应与实际正文一致。
  return text(parseLegacyRichText(value)).replaceAll('\0', '').replace(/\s+/g, ' ').trim();
}
